import type { MemberDto, SessionDto } from "@bafe/api-client";
import type { OrganisationRole } from "@bafe/types";
import {
	AnchoredPanel,
	Button,
	Card,
	EmptyState,
	Field,
	Select,
	StatusPill,
	cn,
	useToast,
} from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generateAccountPassword } from "@bafe/validation";
import { MoreHorizontal, Users } from "lucide-react";
import { type FormEvent, useId, useRef, useState } from "react";

import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PasswordField } from "../../components/PasswordField";
import { Popup } from "../../components/Popup";
import { ErrorPanel } from "../../components/ErrorPanel";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { fieldError } from "../../lib/errors";
import { formatRelative } from "../../lib/format";
import { queryKeys } from "../../lib/query";
import { activeMembership } from "../../lib/session";
import { useOrganisationStore } from "../../lib/stores";
import { ROLE_DESCRIPTION, ROLE_LABEL } from "../../lib/status";

const INVITABLE_ROLES: OrganisationRole[] = ["admin", "user", "billing", "support_viewer"];

/** Roles an owner may assign from the member row (Owner transfers via dedicated API). */
const ASSIGNABLE_MEMBER_ROLES: OrganisationRole[] = ["owner", ...INVITABLE_ROLES];

const ICON_BUTTON =
	"inline-flex size-10 shrink-0 items-center justify-center rounded-(--radius-md) border-0 bg-transparent text-(--text-secondary) transition-colors duration-150 hover:bg-(--surface-sunken) hover:text-(--text-primary) focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-brand-500)";

const TASK_ACCESS_HINT =
	"Can see every task in this workspace. Does not grant editing or administrator access.";

function MemberTaskAccess({
	member,
	displayName,
	canManage,
	pending,
	open,
	onOpenChange,
	onChange,
}: {
	member: MemberDto;
	displayName: string;
	canManage: boolean;
	pending: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onChange: (viewAllHubTasks: boolean) => void;
}) {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelId = useId();
	const included = member.role === "owner" || member.role === "admin";
	const enabled = member.viewAllHubTasks === true;

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				className={cn(ICON_BUTTON, open && "bg-(--surface-sunken) text-(--text-primary)")}
				aria-label={`Task access for ${displayName}`}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-controls={panelId}
				onClick={() => onOpenChange(!open)}
			>
				<Users className="size-4" aria-hidden="true" />
			</button>
			<AnchoredPanel
				open={open}
				anchorRef={triggerRef}
				align="end"
				onDismiss={() => onOpenChange(false)}
				id={panelId}
				role="dialog"
				aria-label={`Task access for ${displayName}`}
				className="w-72 p-3"
			>
				<div className="flex items-start justify-between gap-3">
					<p className="text-sm font-medium text-(--text-primary)">View All Tasks</p>
					{included ? (
						<p className="shrink-0 pt-0.5 text-xs text-(--text-muted)">Included with role</p>
					) : (
						<button
							type="button"
							role="switch"
							aria-checked={enabled}
							aria-label="View All Tasks"
							disabled={!canManage || pending}
							onClick={() => onChange(!enabled)}
							className={cn(
								"relative h-[18px] w-8 shrink-0 rounded-full border-0 p-0 transition-colors",
								"after:absolute after:top-0.5 after:left-0.5 after:block after:size-3.5 after:rounded-full after:bg-white after:shadow after:transition-transform",
								enabled ? "bg-zinc-700 after:translate-x-3.5" : "bg-zinc-200",
								"disabled:cursor-default disabled:opacity-55",
							)}
						/>
					)}
				</div>
				<p className="mt-1.5 text-xs leading-5 text-(--text-muted)">{TASK_ACCESS_HINT}</p>
			</AnchoredPanel>
		</>
	);
}

function MemberActionsMenu({
	label,
	items,
}: {
	label: string;
	items: Array<{ id: string; label: string; onSelect: () => void; danger?: boolean }>;
}) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuId = useId();

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				className={cn(ICON_BUTTON, open && "bg-(--surface-sunken) text-(--text-primary)")}
				aria-label={label}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={menuId}
				onClick={() => setOpen((current) => !current)}
			>
				<MoreHorizontal className="size-4" aria-hidden="true" />
			</button>
			<AnchoredPanel
				open={open}
				anchorRef={triggerRef}
				align="end"
				onDismiss={() => setOpen(false)}
				id={menuId}
				role="menu"
				aria-label={label}
				className="min-w-44 py-1"
			>
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						role="menuitem"
						onClick={() => {
							setOpen(false);
							item.onSelect();
						}}
						className={cn(
							"flex w-full rounded-(--radius-sm) px-2.5 py-2 text-left text-sm outline-none",
							item.danger ? "text-(--color-critical-700)" : "text-(--text-primary)",
							"hover:bg-(--surface-sunken) focus-visible:bg-(--surface-sunken)",
						)}
					>
						{item.label}
					</button>
				))}
			</AnchoredPanel>
		</>
	);
}

export function TeamSection({ session }: { session: SessionDto }) {
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const membership = activeMembership(session, organisationId);
	const isOwner = membership?.role === "owner";
	const canManageAccess = isOwner || membership?.role === "admin";
	const [inviteOpen, setInviteOpen] = useState(false);

	const members = useQuery({ queryKey: queryKeys.members, queryFn: () => api.listMembers() });

	return (
		<div className="flex flex-col gap-6">
			<div className="row head-actions">
				<p className="sub">People who can sign in to this workspace. They are not mailboxes.</p>
				<button className="btn" type="button" onClick={() => setInviteOpen(true)}>
					New access
				</button>
			</div>

			<QueryState query={members} errorTitle="We could not load your team.">
				{(list) => (
					<MemberList
						members={list.filter((member) => member.role !== "support_viewer")}
						session={session}
						isOwner={isOwner}
						canManageAccess={canManageAccess}
						onInvite={() => setInviteOpen(true)}
					/>
				)}
			</QueryState>

			<InviteMember open={inviteOpen} onClose={() => setInviteOpen(false)} />
		</div>
	);
}

function MemberList({
	members,
	session,
	isOwner,
	canManageAccess,
	onInvite,
}: {
	members: MemberDto[];
	session: SessionDto;
	isOwner: boolean;
	canManageAccess: boolean;
	onInvite: () => void;
}) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [removing, setRemoving] = useState<MemberDto | null>(null);
	const [provisioning, setProvisioning] = useState<MemberDto | null>(null);
	const [accessOpenId, setAccessOpenId] = useState<string | null>(null);
	const [transferTarget, setTransferTarget] = useState<MemberDto | null>(null);
	const [transferConfirmName, setTransferConfirmName] = useState("");

	const organisation = useQuery({
		queryKey: queryKeys.organisation,
		queryFn: () => api.getOrganisation(),
		enabled: transferTarget !== null,
	});
	const organisationName = organisation.data?.name ?? "";

	const changeRole = useMutation({
		mutationFn: (input: { userId: string; role: OrganisationRole }) =>
			api.changeMemberRole(input.userId, input.role),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.members });
			toast({ tone: "success", message: "Role updated." });
		},
	});

	const changeTaskAccess = useMutation({
		mutationFn: (input: { userId: string; viewAllHubTasks: boolean }) =>
			api.setMemberHubTaskAccess(input.userId, input.viewAllHubTasks),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.members });
			toast({ tone: "success", message: "Task access updated." });
		},
	});

	const remove = useMutation({
		mutationFn: (member: MemberDto) =>
			member.userId ? api.removeMember(member.userId) : api.cancelInvitation(member.id),
		onSuccess: (_result, member) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.members });
			setRemoving(null);
			toast({
				tone: "success",
				message: member.userId ? "That person no longer has access." : "That invitation was cancelled.",
			});
		},
	});

	const transferOwnership = useMutation({
		mutationFn: (input: { userId: string; confirmOrganisationName: string }) =>
			api.transferOwnership(input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.members });
			void queryClient.invalidateQueries({ queryKey: queryKeys.session });
			setTransferTarget(null);
			setTransferConfirmName("");
			toast({ tone: "success", message: "Ownership transferred." });
		},
	});

	if (members.length === 0) {
		return (
			<Card>
				<EmptyState
					title="You are the only person here"
					description="Invite an administrator to help manage domains and email accounts, or a billing contact for invoices."
					action={
						<Button variant="primary" onClick={onInvite}>
							New access
						</Button>
					}
				/>
			</Card>
		);
	}

	return (
		<>
			{changeRole.isError && <ErrorPanel error={changeRole.error} title="We could not change that role." />}
			{transferOwnership.isError && (
				<ErrorPanel error={transferOwnership.error} title="We could not transfer ownership." />
			)}
			{changeTaskAccess.isError && <ErrorPanel error={changeTaskAccess.error} title="We could not update task access." />}

			<Card flush>
				<ul>
					{members.map((member) => {
						const isSelf = member.userId === session.user.id;
						const pendingInvite = member.userId === "";
						const displayName = member.fullName || member.email;
						const canEdit = isOwner && !isSelf && member.role !== "owner" && !pendingInvite;
						const canRemove = canEdit || (isOwner && pendingInvite);
						const canProvision = canManageAccess && pendingInvite;

						return (
							<li
								key={member.id}
								className="flex flex-col gap-2 border-b border-(--border-subtle) px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
							>
								<div className="flex min-w-0 flex-1 flex-col gap-0.5">
									<div className="flex flex-wrap items-center gap-2">
										<p className="text-sm font-medium text-(--text-primary)">{displayName}</p>
										{isSelf && <StatusPill tone="info">You</StatusPill>}
										{member.status === "invited" && <StatusPill tone="pending">Invited</StatusPill>}
									</div>
									{member.fullName ? (
										<p className="text-sm text-(--text-secondary)">{member.email}</p>
									) : null}
									<p className="text-xs text-(--text-muted)">
										{pendingInvite
											? "Has not joined yet"
											: member.lastLoginAt
												? `Last signed in ${formatRelative(member.lastLoginAt)}`
												: "Has not signed in yet"}
									</p>
								</div>

								<div className="flex w-full min-w-0 items-center gap-1.5 sm:w-auto sm:justify-end">
									{canEdit ? (
										<div className="min-w-0 flex-1 sm:w-36 sm:flex-none">
											<Select
												value={member.role}
												aria-label={`Role for ${displayName}`}
												onChange={(event) => {
													const role = event.target.value as OrganisationRole;
													if (role === "owner") {
														setTransferTarget(member);
														setTransferConfirmName("");
														return;
													}
													changeRole.mutate({ userId: member.userId, role });
												}}
											>
												{ASSIGNABLE_MEMBER_ROLES.map((role) => (
													<option key={role} value={role}>
														{ROLE_LABEL[role]}
													</option>
												))}
											</Select>
										</div>
									) : (
										<span className="min-w-0 flex-1 truncate px-1 text-sm text-(--text-secondary) sm:flex-none">
											{ROLE_LABEL[member.role]}
										</span>
									)}

									{!pendingInvite ? (
										<MemberTaskAccess
											member={member}
											displayName={displayName}
											canManage={canManageAccess}
											pending={changeTaskAccess.isPending}
											open={accessOpenId === member.id}
											onOpenChange={(next) => setAccessOpenId(next ? member.id : null)}
											onChange={(viewAllHubTasks) => {
												if (!member.userId) return;
												changeTaskAccess.mutate({ userId: member.userId, viewAllHubTasks });
											}}
										/>
									) : null}

									{canProvision || canRemove ? (
										<MemberActionsMenu
											label={`Actions for ${displayName}`}
											items={[
												...(canProvision
													? [
															{
																id: "provision",
																label: "Set password & activate",
																onSelect: () => setProvisioning(member),
															},
														]
													: []),
												...(canRemove
													? [
															{
																id: "remove",
																label: pendingInvite ? "Cancel invitation" : "Remove",
																danger: true,
																onSelect: () => setRemoving(member),
															},
														]
													: []),
											]}
										/>
									) : null}
								</div>
							</li>
						);
					})}
				</ul>
			</Card>

			<ProvisionInvitation
				member={provisioning}
				onClose={() => setProvisioning(null)}
			/>

			<ConfirmDialog
				open={removing !== null}
				onClose={() => setRemoving(null)}
				title={
					removing
						? removing.userId
							? `Remove ${removing.fullName}?`
							: `Cancel the invitation for ${removing.email}?`
						: "Remove this person"
				}
				description={
					removing?.userId
						? "They lose access to this organisation immediately. Mailboxes and mail are not affected."
						: "The invitation link will stop working. You can send a new one afterwards."
				}
				confirmLabel={removing?.userId ? "Remove access" : "Cancel invitation"}
				danger
				pending={remove.isPending}
				error={remove.error}
				onConfirm={() => {
					if (removing) remove.mutate(removing);
				}}
			/>

			<ConfirmDialog
				open={transferTarget !== null}
				onClose={() => {
					setTransferTarget(null);
					setTransferConfirmName("");
					transferOwnership.reset();
				}}
				title={
					transferTarget
						? `Make ${transferTarget.fullName || transferTarget.email} the owner?`
						: "Transfer ownership"
				}
				description="This takes effect immediately. You become an administrator and lose billing control."
				confirmLabel="Transfer ownership"
				danger
				pending={transferOwnership.isPending}
				error={transferOwnership.error}
				confirmPhrase={organisationName}
				confirmPhraseLabel={`Type ${organisationName} to confirm`}
				confirmValue={transferConfirmName}
				onConfirmValueChange={setTransferConfirmName}
				onConfirm={() => {
					if (!transferTarget?.userId) return;
					transferOwnership.mutate({
						userId: transferTarget.userId,
						confirmOrganisationName: transferConfirmName.trim(),
					});
				}}
			/>
		</>
	);
}

function ProvisionInvitation({ member, onClose }: { member: MemberDto | null; onClose: () => void }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [submitted, setSubmitted] = useState(false);

	const accountExists = member?.accountExists === true;
	const open = member !== null;

	const provision = useMutation({
		mutationFn: () =>
			api.provisionInvitation(member!.id, accountExists ? {} : { password }),
		onSuccess: (activated) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.members });
			setPassword("");
			setConfirmPassword("");
			setSubmitted(false);
			onClose();
			toast({
				tone: "success",
				message: accountExists
					? `${activated.email} can sign in with their existing Key password.`
					: `${activated.email} is active. Share the password securely — they must change it on first sign-in.`,
			});
		},
	});

	function close() {
		if (provision.isPending) return;
		setPassword("");
		setConfirmPassword("");
		setSubmitted(false);
		provision.reset();
		onClose();
	}

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitted(true);
		if (accountExists) {
			provision.mutate();
			return;
		}
		if (password !== confirmPassword) return;
		provision.mutate();
	}

	const mismatch = submitted && !accountExists && password !== confirmPassword;

	return (
		<Popup open={open} title="Set password & activate" onClose={close}>
			<form onSubmit={submit} noValidate>
				<p className="sub">
					{accountExists
						? "This person already has a Key account. They keep their password and gain access to this workspace."
						: "Create their account now. They can sign in immediately and must choose a new password on first sign-in."}
				</p>
				{provision.isError && (
					<p className="err">{fieldError(provision.error, "password") ?? "We could not activate that account."}</p>
				)}
				<label htmlFor="provision-email">Email address</label>
				<input id="provision-email" type="email" value={member?.email ?? ""} readOnly />
				{!accountExists && (
					<>
						<PasswordField
							label="Password"
							value={password}
							onChange={setPassword}
							autoComplete="new-password"
							required
							action={
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => {
										const generated = generateAccountPassword();
										setPassword(generated);
										setConfirmPassword(generated);
									}}
								>
									Generate strong password
								</Button>
							}
						/>
						<PasswordField
							label="Confirm password"
							value={confirmPassword}
							onChange={setConfirmPassword}
							error={mismatch ? "Passwords do not match." : null}
							autoComplete="new-password"
							required
						/>
					</>
				)}
				<p className="actions">
					<button className="btn" type="submit" disabled={provision.isPending}>
						{provision.isPending ? "Activating" : "Activate account"}
					</button>
					<button className="btn ghost" type="button" onClick={close}>
						Cancel
					</button>
				</p>
			</form>
		</Popup>
	);
}

function InviteMember({ open, onClose }: { open: boolean; onClose: () => void }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [email, setEmail] = useState("");
	const [role, setRole] = useState<OrganisationRole>("admin");

	const invite = useMutation({
		mutationFn: () => api.inviteMember({ email: email.trim(), role }),
		onSuccess: (member) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.members });
			setEmail("");
			setRole("admin");
			onClose();
			toast({ tone: "success", message: `We sent an invitation to ${member.email}.` });
		},
	});

	function close() {
		if (invite.isPending) return;
		onClose();
	}

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (email.trim().length === 0) return;
		invite.mutate();
	}

	return (
		<Popup open={open} title="New access" onClose={close}>
			<form onSubmit={submit} noValidate>
				<p className="sub">They get an email with a link to join this workspace.</p>
				{invite.isError && (
					<p className="err">{fieldError(invite.error, "email") ?? "We could not send that invitation."}</p>
				)}
				<label htmlFor="access-email">Email address</label>
				<input
					id="access-email"
					type="email"
					value={email}
					required
					autoComplete="off"
					autoCapitalize="none"
					spellCheck={false}
					autoFocus
					onChange={(event) => setEmail(event.target.value)}
				/>
				<Field label="Role">
					{({ id }) => (
						<Select
							id={id}
							value={role}
							onChange={(event) => setRole(event.target.value as OrganisationRole)}
						>
							{INVITABLE_ROLES.map((item) => (
								<option key={item} value={item}>
									{ROLE_LABEL[item]}
								</option>
							))}
						</Select>
					)}
				</Field>
				<p className="sub">{ROLE_DESCRIPTION[role]}</p>
				<p className="actions">
					<button className="btn" type="submit" disabled={invite.isPending}>
						{invite.isPending ? "Sending" : "Send invitation"}
					</button>
					<button className="btn ghost" type="button" onClick={close}>
						Cancel
					</button>
				</p>
			</form>
		</Popup>
	);
}
